import keys from '@debbie/common/dist/cache/keys.js';
import { AutoBillingTiming, Timing, } from '@debbie/common/dist/economy/billing-appendix-setups/index.js';
import { VoucherCategory } from '@debbie/common/dist/economy/vouchers/index.js';
import { isAdmin } from '@debbie/common/dist/permissions/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as billingAppendixSetupsAPI from '@debbie/cortex/dist/api/billing-appendix-setups.js';
import { billingAppendixSetupsEntity } from '@debbie/cortex/dist/billing-appendix-setups/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useRole } from '@debbie/cortex/dist/me/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useVoucherTypes } from '@debbie/cortex/dist/voucher-types/index.js';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import { Button, Checkbox, Input } from '..//index.js';
import { Col, Row } from '../Grid/index.js';
import { PlusCircle, XCircle } from '../Icons/index.js';
import SelectDOM from '../Select.js';
const selectableVoucherTypeCategories = [
    VoucherCategory.FEE,
    VoucherCategory.DISBURSEMENT,
    VoucherCategory.OVERPAYMENT,
    VoucherCategory.VAT,
    VoucherCategory.PAYOUT,
    VoucherCategory.COLLECTIONCOMMISSION,
];
const BillingAppendixSetup = (props) => {
    const { onCreate, editable: _editable, billingAppendixSetup } = props;
    const lang = useSelector((s) => s.lang);
    const meRole = useRole();
    const voucherTypesLoadable = useVoucherTypes();
    const economyVoucherTypes = h.hasData(voucherTypesLoadable)
        ? voucherTypesLoadable.data
        : null;
    const initAppendices = () => {
        return (billingAppendixSetup?.appendices || [
            {
                handle: lang.billingAppendixSetups.billing,
                timing: { type: Timing.ASAP, details: {} },
                onlyVoucherTypeIds: (economyVoucherTypes || [])
                    .filter((voucherType) => selectableVoucherTypeCategories.includes(voucherType.category))
                    .map((voucherType) => voucherType.voucherTypeId),
                billingAppendixId: crypto.randomUUID(),
                includeVatInDepositShare: false,
                hideFromCreditor: false,
                autoBillingTiming: null,
            },
        ]);
    };
    const [name, setName] = useState(billingAppendixSetup?.name || '');
    const [appendices, setAppendices] = useState([]);
    const updateSetup = (i, update) => setAppendices(appendices.map((f, index) => i === index
        ? {
            ...f,
            ...update,
        }
        : f));
    const updateNameMutation = useMutation(async () => {
        await billingAppendixSetupsAPI.patch(billingAppendixSetup?.billingAppendixSetupId, { name });
        q.invalidate(keys.write.modify(billingAppendixSetupsEntity, billingAppendixSetup.billingAppendixSetupId));
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.billingAppendixSetup);
        },
    });
    const updateAppendicesMutation = useMutation(async () => {
        if (!billingAppendixSetup?.billingAppendixSetupId) {
            throw new Error('billingAppendixSetup.billingAppendixSetupId is required');
        }
        await billingAppendixSetupsAPI.patch(billingAppendixSetup?.billingAppendixSetupId, { appendices });
        q.invalidate(keys.write.modify(billingAppendixSetupsEntity, billingAppendixSetup.billingAppendixSetupId));
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.billingAppendixSetup);
        },
    });
    const createMutation = useMutation(async () => {
        const newBillingAppendixSetup = await billingAppendixSetupsAPI.create({
            name,
            appendices: appendices,
        });
        q.invalidate(keys.write.create(billingAppendixSetupsEntity));
        props.onCreate && props.onCreate(newBillingAppendixSetup);
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'create', lang.errorEntities.billingAppendixSetup);
        },
    });
    useEffect(() => {
        if (economyVoucherTypes) {
            setAppendices(initAppendices());
        }
    }, [props.billingAppendixSetup, economyVoucherTypes]);
    if (!economyVoucherTypes) {
        return null;
    }
    const editable = !!((_editable || !!onCreate) &&
        (!props.billingAppendixSetup || !props.billingAppendixSetup.public) &&
        meRole &&
        isAdmin(meRole));
    return (React.createElement("div", null,
        React.createElement(Input, { placeholder: lang.billingAppendixSetups.name, onChange: (e) => setName(e.target.value), value: name, readonly: !editable, onSubmit: billingAppendixSetup?.name !== name &&
                meRole &&
                isAdmin(meRole) &&
                !onCreate
                ? () => updateNameMutation.mutate()
                : undefined }),
        appendices.map((setup, i) => (React.createElement(SetupStyled, { key: i },
            editable && (React.createElement(XCircle, { style: {
                    position: 'absolute',
                    cursor: 'pointer',
                    top: '-7px',
                    left: '-7px',
                }, onClick: () => setAppendices(appendices.filter((_, index) => index !== i)) })),
            React.createElement(Row, null,
                React.createElement(Col, { width: "6" },
                    React.createElement(Input, { disabled: !editable, value: setup.handle, placeholder: lang.billingAppendixSetups.appendixHandle, onChange: (e) => updateSetup(i, { handle: e.target.value }) })),
                React.createElement(Col, { width: "6" },
                    React.createElement(SelectDOM, { disabled: !editable, options: Object.values(Timing).map((timing) => ({
                            value: timing,
                            label: lang.billingAppendixSetupTimings[timing],
                        })), selected: [setup.timing.type], onChange: (selected) => updateSetup(i, {
                            timing: {
                                type: selected[0] ?? setup.timing.type,
                                details: {},
                            },
                        }) })),
                React.createElement(Col, { width: "12" },
                    React.createElement(Checkbox, { placeholder: lang.billingAppendixSetups.hideFromCreditor, disabled: !editable, checked: setup.hideFromCreditor, onChange: (e) => updateSetup(i, {
                            hideFromCreditor: e.target.checked,
                        }) }),
                    React.createElement(Checkbox, { placeholder: lang.billingAppendixSetups.includeVatInDepositShare, disabled: !editable, checked: setup.includeVatInDepositShare, onChange: (e) => updateSetup(i, {
                            includeVatInDepositShare: e.target.checked,
                        }) }),
                    React.createElement(Checkbox, { placeholder: lang.billingAppendixSetups.selectAllVoucherTypes, disabled: !editable, checked: setup.onlyVoucherTypeIds == null, onChange: (e) => updateSetup(i, {
                            onlyVoucherTypeIds: e.target.checked ? null : [],
                        }) }),
                    setup.onlyVoucherTypeIds == null ? null : (React.createElement(Select, { isDisabled: !editable, value: setup.onlyVoucherTypeIds.map((voucherTypeId) => {
                            const voucherType = economyVoucherTypes?.find((voucherType) => voucherType.voucherTypeId === voucherTypeId);
                            return {
                                label: (voucherType &&
                                    lang.pluralEconomyVoucherTypeLabels[voucherType.voucherTypeId]) ||
                                    (voucherType && voucherType.label) ||
                                    '',
                                value: (voucherType && voucherType.voucherTypeId) || '',
                            };
                        }), isMulti: true, placeholder: lang.economy.pipeFilterSelectVoucherTypes, classNamePrefix: "select", options: economyVoucherTypes
                            .filter((voucherType) => selectableVoucherTypeCategories.includes(voucherType.category))
                            .map((voucherType) => ({
                            label: lang.pluralEconomyVoucherTypeLabels[voucherType.voucherTypeId] || voucherType.label,
                            value: voucherType.voucherTypeId,
                        })), onChange: (val) => {
                            updateSetup(i, {
                                onlyVoucherTypeIds: val ? val.map((v) => v.value) : [],
                            });
                        } })),
                    React.createElement(AutoBillingCard, null,
                        React.createElement(AutoBillingHeader, null,
                            React.createElement(AutoBillingTitle, null,
                                React.createElement(AutoBillingDot, { $active: !!setup.autoBillingTiming }),
                                lang.billingAppendixSetups.autoBilling)),
                        React.createElement(AutoBillingDescription, null, lang.billingAppendixSetups.autoBillingDescription),
                        React.createElement(SelectDOM, { disabled: !editable, options: [
                                {
                                    value: '',
                                    label: lang.billingAppendixSetups.autoBillingOff,
                                },
                                ...Object.values(AutoBillingTiming).map((timing) => ({
                                    value: timing,
                                    label: lang.autoBillingTimings[timing],
                                })),
                            ], selected: [setup.autoBillingTiming ?? ''], onChange: (selected) => updateSetup(i, {
                                autoBillingTiming: selected[0] == null || selected[0] === ''
                                    ? null
                                    : selected[0],
                            }) }))))))),
        editable && (React.createElement(React.Fragment, null,
            ' ',
            React.createElement("div", { style: { cursor: 'pointer' }, onClick: () => {
                    setAppendices(
                    // @ts-ignore
                    appendices.concat({
                        handle: lang.billingAppendixSetups.billing,
                        timing: { type: Timing.ASAP, details: {} },
                        onlyVoucherTypeIds: [],
                        billingAppendixId: crypto.randomUUID(),
                        includeVatInDepositShare: false,
                        hideFromCreditor: false,
                        autoBillingTiming: null,
                    }));
                } },
                React.createElement(PlusCircle, { style: {
                        margin: '0.2em 0.2em -0.2em 0.2em',
                    } }),
                ' ',
                lang.billingAppendixSetups.addNewSetup),
            React.createElement("br", null))),
        JSON.stringify(billingAppendixSetup?.appendices || {}) !==
            JSON.stringify(appendices || {}) &&
            meRole &&
            isAdmin(meRole) &&
            _editable ? (React.createElement(Button, { block: true, loading: updateAppendicesMutation.isLoading, onClick: () => updateAppendicesMutation.mutate() }, lang.inputs.save)) : null,
        onCreate && (React.createElement(Button, { block: true, size: "small", loading: createMutation.isLoading, disabled: !(name && appendices.length), onClick: () => createMutation.mutate() }, lang.billingAppendixSetups.createNew))));
};
export default BillingAppendixSetup;
const SetupStyled = styled.div `
  padding: ${THEME.GRID_SPACING}px;
  margin-bottom: ${THEME.GRID_SPACING}px;
  border-radius: 3px;
  background-color: ${THEME.FILL};
  position: relative;
`;
const AutoBillingCard = styled.div `
  margin: ${THEME.GRID_SPACING}px 0 0 0;
  border-radius: 6px;
`;
const AutoBillingHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;
const AutoBillingTitle = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: ${THEME.COLOR_PRIMARY};
  font-size: 0.95em;
`;
const AutoBillingDot = styled.span `
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(p) => p.$active ? THEME.COLOR_ACCENT : THEME.COLOR_GREY};
  box-shadow: ${(p) => p.$active ? `0 0 0 3px ${THEME.ACCENT_SURFACE_STRONG}` : 'none'};
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;
`;
const AutoBillingDescription = styled.div `
  color: ${THEME.COLOR_GREY};
  font-size: 0.8em;
  margin-bottom: ${THEME.GRID_SPACING / 2}px;
  line-height: 1.4;
`;
//# sourceMappingURL=BillingAppendixSetup.js.map